import React from 'react';
import Layout from '../components/layout/Layout';
import '../styles/pages.css';
import usePageMeta from '../hooks/usePageMeta';

// Política de privacidad de la APLICACIÓN MÓVIL SOEM.
// Vive en una URL propia (/privacidad-app) porque Google Play y App Store
// exigen un enlace directo que muestre únicamente la política de la app.
// El contenido refleja lo que la app realmente recolecta, verificado
// contra el código (evidencias, ubicación, audio, perfil) — debe
// mantenerse sincronizado con la declaración de Seguridad de Datos de
// Play Console: si cambia uno, cambia el otro.
//
// 29-sep-2026: se quitó la afirmación de que las evidencias conservan su
// EXIF (la App nunca lo pidió ni nadie lo leía) y se reemplazó por la
// ubicación que la App asocia a cada evidencia; se agregaron Sentry y la
// información agregada de vulnerabilidades (reactivos 35A-D). El apartado 4
// dice "puede registrar" porque la captura por evidencia sale en la Fase 2.

const UPDATED = '29 de septiembre de 2026';

const Section = ({ title, children }) => (
  <div className="legal-section" style={{ marginBottom: '2.5rem' }}>
    <h2>{title}</h2>
    <div className="legal-text">{children}</div>
  </div>
);

const PrivacidadApp = () => {
  usePageMeta({
    title: 'Aviso de Privacidad de la App',
    description: 'Aviso de privacidad de la aplicación móvil SOEM: qué datos recopila, con qué fin y cómo se protegen.',
    path: '/privacidad-app',
  });

  return (
    <Layout>
      <section className="page-header">
        <div className="container">
          <h1>Aviso de Privacidad — Aplicación Móvil SOEM</h1>
          <p>Cómo tratamos los datos personales en la app para iOS y Android</p>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="legal-container">
            <div className="legal-content">

              <Section title="1. Responsable del tratamiento">
                <p>
                  <strong>SOEM Partners Group</strong>, con domicilio en Ciudad de México, México,
                  es responsable del tratamiento de los datos personales que se recaban a través de
                  la aplicación móvil SOEM (en adelante, «la App»), disponible para iOS y Android.
                </p>
                <p>
                  Contacto para asuntos de privacidad: <strong>hola@soempg.com</strong> · Tel. +52 56 3410 3631
                </p>
              </Section>

              <Section title="2. A quién está dirigida la App">
                <p>
                  La App es una herramienta de uso profesional para personal autorizado de SOEM
                  Partners Group y de sus clientes corporativos. <strong>No es una aplicación de
                  consumo y no está dirigida a menores de 18 años.</strong> El acceso es únicamente
                  mediante cuentas creadas por un administrador: la App no permite el registro
                  público de usuarios.
                </p>
              </Section>

              <Section title="3. Qué datos recaba la App y para qué">
                <p>La App recaba únicamente los datos necesarios para su funcionamiento:</p>

                <p><strong>a) Datos de cuenta (obligatorios)</strong><br />
                  Nombre, correo electrónico, rol asignado e identificador de usuario. Se usan para
                  autenticarte, mantener tu sesión iniciada y determinar a qué información tienes
                  acceso según tu rol. Tu contraseña se transmite cifrada y se almacena en nuestros
                  servidores mediante un algoritmo de derivación irreversible (bcrypt): nadie en
                  SOEM puede consultarla.
                </p>

                <p><strong>b) Fotografías y video (opcionales)</strong><br />
                  Con tu autorización expresa, la App accede a la cámara y a la galería para que
                  puedas adjuntar evidencia fotográfica o de video a los criterios de una evaluación
                  de seguridad, y para tu foto de perfil. Solo se transmiten los archivos que tú
                  eliges adjuntar. La App no accede a tu galería en segundo plano ni realiza
                  capturas automáticas. Sobre la ubicación asociada a cada evidencia, ver el
                  apartado 4.
                </p>

                <p><strong>c) Grabaciones de voz (opcionales)</strong><br />
                  Con tu autorización expresa, la App usa el micrófono para que puedas grabar notas
                  de voz como evidencia dentro de una evaluación. La grabación es siempre iniciada
                  por ti y tiene una duración máxima de 60 segundos. La App no graba audio en
                  segundo plano.
                </p>

                <p><strong>d) Ubicación precisa (opcional)</strong><br />
                  Con tu autorización expresa, la App usa el GPS del dispositivo para georreferenciar
                  locaciones y eventos, para asociar a las evidencias y hallazgos el lugar donde se
                  capturaron (ver el apartado 4) y para buscar servicios cercanos (hospitales,
                  gasolineras, bancos, hospedaje). <strong>La ubicación solo se consulta mientras
                  usas la App</strong> — no se recaba en segundo plano. Puedes omitir este permiso:
                  en ese caso capturas las coordenadas o indicas la zona del recinto manualmente, y
                  la App sigue siendo funcional.
                </p>

                <p><strong>e) Actividad dentro de la App</strong><br />
                  Las respuestas de las evaluaciones, notas de texto, calificaciones y los registros
                  de asistencia de personal que captures. Son el objeto mismo del servicio.
                </p>

                <p><strong>f) Información agregada sobre personas en situación de vulnerabilidad</strong><br />
                  Algunas evaluaciones registran si en el evento hay personas con alergias,
                  condiciones médicas informadas, movilidad limitada o alguna discapacidad, junto con
                  cantidades y el tipo de necesidad (por ejemplo, visual, auditiva o motriz). Se usa
                  para planear la accesibilidad, la evacuación y la atención de emergencias. Esta
                  información se captura de forma agregada: <strong>la App no solicita nombres ni
                  datos que identifiquen a esas personas</strong>, y quienes la usan deben abstenerse
                  de incluirlos en las observaciones.
                </p>

                <p><strong>g) Reportes técnicos de fallas</strong><br />
                  Cuando la App presenta un error, envía un reporte técnico para que podamos
                  diagnosticarlo y corregirlo. Puede incluir el tipo de error, la versión de la App,
                  el modelo y el sistema operativo del dispositivo, la pantalla o la operación en
                  la que ocurrió y un identificador de tu cuenta. No se usa con fines publicitarios
                  ni para analizar tu comportamiento.
                </p>

                <p><strong>Datos que la App NO recaba:</strong> no recabamos identificadores
                  publicitarios, no incluimos publicidad, no usamos herramientas de analítica ni de
                  seguimiento de comportamiento, no accedemos a tus contactos, calendario, mensajes
                  ni sensores corporales, y no recabamos información financiera ni de pago a través
                  de la App.
                </p>
              </Section>

              <Section title="4. Ubicación asociada a las evidencias y hallazgos">
                <p>
                  Si concedes el permiso de ubicación, la App puede registrar la ubicación del
                  dispositivo (latitud, longitud y precisión aproximada), junto con la fecha y la
                  hora, en el momento en que capturas o adjuntas una evidencia o registras un
                  hallazgo, así como la zona del recinto que indiques (por ejemplo, acceso, salida
                  de emergencia o estacionamiento).
                </p>
                <p>
                  <strong>Este registro forma parte del servicio:</strong> permite acreditar dónde
                  se capturó cada evidencia y ubicar cada hallazgo dentro del recinto evaluado, lo
                  que da validez documental al reporte de seguridad. Se guarda junto con la
                  evaluación y lo consultan las personas autorizadas del proyecto, incluido el
                  cliente en los reportes publicados.
                </p>
                <p>
                  Si no concedes el permiso, o el GPS no está disponible dentro del recinto, basta
                  con indicar la zona manualmente.
                </p>
                <p>
                  Algunos dispositivos agregan a las fotografías y videos metadatos técnicos (EXIF)
                  que pueden incluir coordenadas y el modelo del equipo. SOEM no utiliza esos
                  metadatos para ubicar las evidencias y no garantiza que se conserven.
                </p>
              </Section>

              <Section title="5. Con quién se comparten los datos">
                <p>
                  No vendemos datos personales ni los compartimos con fines publicitarios. Los
                  compartimos únicamente con los proveedores necesarios para operar el servicio,
                  que los tratan por cuenta nuestra:
                </p>
                <p>
                  · <strong>Cloudinary</strong> — almacenamiento de las fotografías, videos y notas de
                  voz que adjuntas.<br />
                  · <strong>Google Maps Platform</strong> — al usar la búsqueda de servicios cercanos,
                  las coordenadas se envían a Google para obtener los resultados. Aplica la política
                  de privacidad de Google.<br />
                  · <strong>MongoDB Atlas</strong> — base de datos donde se almacena la información
                  operativa.<br />
                  · <strong>SendGrid</strong> — envío de correos transaccionales (recuperación de
                  contraseña y avisos de evaluaciones).<br />
                  · <strong>Sentry</strong> — recepción de los reportes técnicos de fallas de la App
                  (ver el apartado 3, inciso g).<br />
                  · <strong>DigitalOcean</strong> — infraestructura donde se ejecutan nuestros servidores.
                </p>
                <p>
                  También podremos revelar información cuando exista un requerimiento fundado de
                  autoridad competente.
                </p>
              </Section>

              <Section title="6. Seguridad">
                <p>
                  Toda la comunicación entre la App y nuestros servidores viaja cifrada mediante
                  HTTPS/TLS. El acceso a la información está restringido por rol y por asignación:
                  cada usuario solo puede consultar los proyectos, eventos y evaluaciones que le
                  corresponden. Los datos
                  especialmente sensibles del personal (cuentas bancarias, CLABE y número de
                  seguridad social) se almacenan cifrados en la base de datos y se muestran
                  enmascarados. Las sesiones caducan automáticamente.
                </p>
              </Section>

              <Section title="7. Conservación de los datos">
                <p>
                  Conservamos los datos mientras tu cuenta esté activa y, después, durante el plazo
                  necesario para cumplir las obligaciones legales, contractuales y de rendición de
                  cuentas aplicables a los servicios de seguridad contratados. Los reportes de
                  evaluación publicados se conservan como parte del expediente del proyecto del
                  cliente correspondiente.
                </p>
              </Section>

              <Section title="8. Eliminación de tu cuenta y tus datos">
                <p>
                  Puedes solicitar la eliminación de tu cuenta directamente desde la App, en
                  <strong> Perfil → Eliminar cuenta</strong>. Al hacerlo, tu cuenta se desactiva de
                  forma inmediata y permanente, se cierran tus sesiones y pierdes el acceso al
                  servicio.
                </p>
                <p>
                  También puedes solicitarlo escribiendo a <strong>hola@soempg.com</strong> desde el
                  correo asociado a tu cuenta. Atenderemos tu solicitud en un plazo máximo de 20
                  días hábiles.
                </p>
                <p>
                  Ten en cuenta que las evaluaciones de seguridad ya publicadas y los registros de
                  auditoría forman parte del expediente contratado por el cliente y de nuestras
                  obligaciones de rendición de cuentas, por lo que se conservan de forma disociada
                  aun después de eliminar tu cuenta.
                </p>
              </Section>

              <Section title="9. Tus derechos (ARCO)">
                <p>
                  Conforme a la Ley Federal de Protección de Datos Personales en Posesión de los
                  Particulares, tienes derecho a <strong>acceder</strong> a tus datos personales,
                  <strong> rectificarlos</strong> cuando sean inexactos, <strong>cancelarlos</strong> y
                  <strong> oponerte</strong> a su tratamiento, así como a revocar el consentimiento
                  que nos hayas otorgado.
                </p>
                <p>
                  Para ejercer cualquiera de estos derechos escribe a <strong>hola@soempg.com</strong>{' '}
                  indicando tu nombre, el derecho que deseas ejercer y un medio de contacto.
                  Responderemos en un plazo máximo de 20 días hábiles.
                </p>
                <p>
                  Los permisos de cámara, micrófono y ubicación pueden revocarse en cualquier momento
                  desde los ajustes de tu dispositivo, sin que ello impida usar el resto de la App.
                </p>
              </Section>

              <Section title="10. Cambios a este aviso">
                <p>
                  Podemos actualizar este aviso para reflejar cambios en la App o en la normativa
                  aplicable. La versión vigente estará siempre disponible en esta misma dirección y
                  se indicará la fecha de la última actualización.
                </p>
              </Section>

              <p className="legal-date">Última actualización: {UPDATED}</p>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default PrivacidadApp;
